// apps/website/src/main.tsx

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { HelmetProvider } from 'react-helmet-async';
import { ThemeProvider } from '@dopomogai/ui';

// Import Supabase and API Client tools
import { initSupabase, type SupabaseClient } from '@dopomogai/supabase-client';
import { configureApiClient } from '@dopomogai/api-client';

// Import i18next
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n';
import { getPublicConfigError } from './config/site';

// --- Get & Validate Environment Variables ---
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const apiUrl = import.meta.env.VITE_API_URL;
const rootElement = document.getElementById('root');

const publicConfigError = getPublicConfigError(supabaseUrl, supabaseAnonKey, apiUrl);

if (publicConfigError) {
  if (rootElement) {
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <ThemeProvider defaultTheme="neobrutalism">
          <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center px-6">
            <div className="max-w-xl rounded-2xl border-2 border-black bg-white p-8 shadow-[8px_8px_0_0_rgba(0,0,0,1)]">
              <p className="text-xs font-black uppercase tracking-[0.3em] text-rose-600">Configuration Error</p>
              <h1 className="mt-4 text-3xl font-extrabold">Website boot blocked</h1>
              <p className="mt-4 text-sm leading-6 text-slate-700">{publicConfigError}</p>
              <p className="mt-3 text-sm leading-6 text-slate-700">
                Update the website env and reload the page.
              </p>
            </div>
          </div>
        </ThemeProvider>
      </React.StrictMode>
    );
  }

  throw new Error(publicConfigError);
}

// ===================================================================
// Initialize clients once, and instrument debugging
// ===================================================================

const supabase: SupabaseClient = initSupabase(supabaseUrl, supabaseAnonKey);

const shouldEnableDebug =
  import.meta.env.DEV ||
  (typeof window !== 'undefined' &&
    (window.localStorage.getItem('dopomogai:debug') === '1' ||
      new URLSearchParams(window.location.search).get('dgDebug') === '1'));

// Debug flag
;(window as any).__DG_DEBUG = shouldEnableDebug;

if (shouldEnableDebug) {
  window.addEventListener('visibilitychange', () => {
    if ((window as any).__DG_DEBUG) console.log('[vis]', document.visibilityState, Date.now());
  });
  window.addEventListener('focus', () => {
    if ((window as any).__DG_DEBUG) console.log('[focus] at', Date.now());
  });
}

// Expose supabase instance for identity comparison
;(window as any).__DG_SUPABASE = supabase;

// Auth events log
supabase.auth.onAuthStateChange((event, session) => {
  if ((window as any).__DG_DEBUG) {
    console.log('[auth:event]', event, {
      hasSession: !!session,
      expires_at: session?.expires_at,
      sub: session?.user?.id?.slice?.(0, 8),
    });
  }
});

// Monkey-patch getSession to measure timing
if (shouldEnableDebug) {
  const orig = supabase.auth.getSession.bind(supabase.auth);
  supabase.auth.getSession = async () => {
    const start = Date.now();
    if ((window as any).__DG_DEBUG) console.log('[getSession:start]');
    try {
      const out = await orig();
      if ((window as any).__DG_DEBUG)
        console.log('[getSession:ok]', Date.now() - start, 'ms', !!out?.data?.session);
      return out;
    } catch (e) {
      if ((window as any).__DG_DEBUG) console.warn('[getSession:err]', Date.now() - start, 'ms', e);
      throw e;
    } finally {
      if ((window as any).__DG_DEBUG) console.log('[getSession:end]', Date.now() - start, 'ms');
    }
  };
}

// Optional: instrument fetch to see network traffic (including supabase refresh)
if (shouldEnableDebug) {
  const origFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url = typeof input === 'string' ? input : (input as Request).url ?? String(input);
    if ((window as any).__DG_DEBUG) console.debug('[fetch]', init?.method || 'GET', url);
    const res = await origFetch(input, init);
    if ((window as any).__DG_DEBUG) console.debug('[fetch:res]', res.status, res.url);
    return res;
  };
}

// Configure API client after instrumentation (interceptor will see __DG_DEBUG)
configureApiClient({
  apiBaseUrl: apiUrl,
  supabaseClient: supabase,
});

if (shouldEnableDebug) {
  setTimeout(() => {
    const same = !!(window as any).__DG_API_AXIOS;
    console.log('[diag] api axios present?', same);
  }, 0);
}

// --- Render Application ---
ReactDOM.createRoot(rootElement!).render(
  <React.StrictMode>
    <ThemeProvider defaultTheme="neobrutalism">
      <HelmetProvider>
        <BrowserRouter>
          <I18nextProvider i18n={i18n}>
            <App supabaseClient={supabase} />
          </I18nextProvider>
        </BrowserRouter>
      </HelmetProvider>
    </ThemeProvider>
  </React.StrictMode>
);
